import { useState, useEffect, useRef } from "react";
import "./Login.css";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import logo from "../../assets/peoflo_logo.svg";

const resolveError = (error) => {
  const serverMessage = error.response?.data?.error;

  // 423 — account locked (your custom lockout)
  if (error.response?.status === 423) {
    return {
      message: serverMessage || "Account is locked. Try again later.",
      type: "rate-limit",
    };
  }

  // 429 — IP rate limit
  if (error.response?.status === 429) {
    const retryAfter = error.response.headers["retry-after"];
    const mins = retryAfter ? Math.ceil(retryAfter / 60) : 10;
    return {
      message:
        serverMessage ||
        `Too many attempts. Try again in ${mins} minute${mins !== 1 ? "s" : ""}.`,
      type: "rate-limit",
    };
  }

  // 401 — wrong password (backend sends remaining attempts message)
  if (error.response?.status === 401)
    return {
      message: serverMessage || "Invalid email or password.",
      type: "auth",
    };

  if (error.response?.status === 403)
    return {
      message: serverMessage || "This account doesn't have admin access.",
      type: "auth",
    };

  if (error.response?.status === 500)
    return {
      message: "Server error. Please try again shortly.",
      type: "server",
    };

  if (error.code === "ERR_NETWORK" || error.message === "Network Error")
    return {
      message: "No internet connection. Check your network.",
      type: "network",
    };

  const firebaseErrors = {
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/user-not-found": "No account found with this email.",
    "auth/wrong-password": "Incorrect password.",
    "auth/invalid-email": "Enter a valid email address.",
    "auth/user-disabled": "This account has been disabled.",
    "auth/too-many-requests": "Too many attempts. Account temporarily locked.",
    "auth/network-request-failed": "Network error. Check your connection.",
    "auth/internal-error": "Something went wrong. Please try again.",
  };

  if (error.code && firebaseErrors[error.code])
    return { message: firebaseErrors[error.code], type: "auth" };

  return {
    message: serverMessage || "Sign in failed. Please try again.",
    type: "unknown",
  };
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const [success, setSuccess] = useState(false);

  const cardRef = useRef(null);
  const bannerRef = useRef(null);
  const countdownRef = useRef(null);
  const errorTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (error) setError(null);
    if (fieldErrors.email || fieldErrors.password)
      setFieldErrors({ email: "", password: "" });
  }, [email, password]);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;

    if (error) {
      banner.className = `login-error-banner login-error-${error.type} visible`;

      if (error.type !== "rate-limit") {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = setTimeout(() => {
          banner.classList.remove("visible");

          setTimeout(() => setError(null), 300);
        }, 3000);
      }
    } else {
      banner.className = "login-error-banner";
    }

    return () => clearTimeout(errorTimerRef.current);
  }, [error]);

  useEffect(() => {
    if (rateLimitCountdown <= 0) return;
    countdownRef.current = setInterval(() => {
      setRateLimitCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setError(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [rateLimitCountdown]);

  const validate = () => {
    const errors = { email: "", password: "" };
    let valid = true;
    if (!email.trim()) {
      errors.email = "Email is required.";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Enter a valid email address.";
      valid = false;
    }
    if (!password) {
      errors.password = "Password is required.";
      valid = false;
    } else if (password.length < 6) {
      errors.password = "Must be at least 6 characters.";
      valid = false;
    }
    setFieldErrors(errors);
    return valid;
  };

  const triggerShake = () => {
    const card = cardRef.current;
    if (!card) return;
    card.classList.remove("shake");
    void card.offsetWidth;
    card.classList.add("shake");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validate()) {
      triggerShake();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await api.post("/auth/login", { email, password });

      localStorage.setItem("role", data.role);
      localStorage.setItem("email", data.email);
      localStorage.setItem("name", data.name);

      setSuccess(true);

      await signInWithEmailAndPassword(auth, email, password);

    } catch (err) {
      setSuccess(false);
      setLoading(false); // 👈 only reset on failure
      await auth.signOut();
      const resolved = resolveError(err);
      setError(resolved);
      if (err.response?.status === 423) {
        const lockedUntil = err.response?.data?.lockedUntil;
        if (lockedUntil) {
          const secsLeft = Math.ceil(
            (new Date(lockedUntil) - new Date()) / 1000,
          );
          setRateLimitCountdown(secsLeft > 0 ? secsLeft : 0);
        }
      } else if (resolved.type === "rate-limit") {
        const retryAfter = err.response?.headers["retry-after"];
        setRateLimitCountdown(retryAfter ? parseInt(retryAfter) : 10 * 60);
      }
      triggerShake();
    }
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
  };

  const isDisabled = loading || rateLimitCountdown > 0;

  if (success)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f7f9f7",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            border: "3px solid #e0e0e0",
            borderTopColor: "#006663",
            borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }}
        />
      </div>
    );

  return (
    <div className="login-page">
      <div
        className="login-card"
        ref={cardRef}
        onAnimationEnd={(e) => {
          if (e.animationName === "shake")
            cardRef.current?.classList.remove("shake");
        }}
      >
        {/* Logo + Title */}
        <div className="login-header">
          <div className="login-header-logo">
            <img src={logo} alt="Peoflo Logo" className="sidebar-logo" />
            <span className="sidebar-title">| Admin</span>
          </div>
          <h1>Sign in to continue</h1>
        </div>

        {/* Error banner — always in DOM, never causes layout shift */}
        <div className="login-error-banner" ref={bannerRef} aria-live="polite">
          <span className="material-symbols-outlined error-icon">
            {error?.type === "network"
              ? "wifi_off"
              : error?.type === "rate-limit"
                ? "timer"
                : error?.type === "server"
                  ? "dns"
                  : "error"}
          </span>
          <div>
            <span>{error?.message}</span>
            {rateLimitCountdown > 0 && (
              <span className="countdown">
                Retry in {formatCountdown(rateLimitCountdown)}
              </span>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} noValidate>
          {/* Email */}
          <div
            className={`login-field ${fieldErrors.email ? "has-error" : ""}`}
          >
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@peoflo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
            <span className="field-error-msg">{fieldErrors.email || ""}</span>
          </div>

          {/* Password */}
          <div
            className={`login-field ${fieldErrors.password ? "has-error" : ""}`}
          >
            <div className="label-row">
              <label htmlFor="password">Password</label>
            </div>
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="toggle-pw"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <span className="material-symbols-outlined">
                  {showPassword ? "visibility_off" : "visibility"}
                </span>
              </button>
            </div>
            <span className="field-error-msg">
              {fieldErrors.password || ""}
            </span>
          </div>

          {/* Submit */}
          <button type="submit" className="login-btn" disabled={isDisabled}>
            {loading ? (
              <span className="login-spinner" />
            ) : rateLimitCountdown > 0 ? (
              `Locked · ${formatCountdown(rateLimitCountdown)}`
            ) : (
              "Sign in"
            )}
          </button>
        </form>

        <p className="login-footer">
          Having trouble? Contact your super admin.
        </p>
      </div>
    </div>
  );
};

export default Login;
